// Usage:
// const { fetchLedgers, createLedger, updateLedger, toggleLedger, userCode } = useMiniERP();
//
// useEffect(() => {
//   const load = async () => {
//     const res = await fetchLedgers();
//     setLedgers(res?.data || []);
//   };
//   load();
// }, []);
//
// Every function below is a plain async call — it resolves to the API
// response ({ success, message, data }) or throws. There is NO .execute()
// / .data state wrapper — pages must keep their own local state via useState
// and refresh it by calling the fetch* function again after a mutation.

import { useSelector } from "react-redux";
import useFetch from "../../../hooks/useFetch";

export function useMiniERP() {
  const authState = useSelector((state) => state.authReducer || {});
  const [fetchData] = useFetch();
  const token = authState.token;

  const userId = authState?.user?._id;
  const userName = authState?.user?.name;
  const userCode = authState?.user?.think5whyscode;

  // ====================== ACCOUNTING PERIOD ======================
  const fetchAccountingPeriods = async () => {
    const config = { url: "/minierp/periods", method: "get", headers: { Authorization: token } };
    return fetchData(config, { silent: true });
  };

  const createAccountingPeriod = async (payload) => {
    const config = { url: "/minierp/periods", method: "post", data: payload, headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };

  const updateAccountingPeriod = async (id, payload) => {
    const config = { url: `/minierp/periods/${id}`, method: "put", data: payload, headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };

  const toggleAccountingPeriod = async (id) => {
    const config = { url: `/minierp/periods/${id}/toggle`, method: "patch", headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };

  // ====================== LEDGER ======================
  const fetchLedgers = async (group = null) => {
    const config = {
      url: group ? `/minierp/ledgers?group=${group}` : "/minierp/ledgers",
      method: "get",
      headers: { Authorization: token }
    };
    return fetchData(config, { silent: true });
  };

  const createLedger = async (payload) => {
    const config = { url: "/minierp/ledgers", method: "post", data: payload, headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };

  const updateLedger = async (id, payload) => {
    const config = { url: `/minierp/ledgers/${id}`, method: "put", data: payload, headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };

  const toggleLedger = async (id) => {
    const config = { url: `/minierp/ledgers/${id}/toggle`, method: "patch", headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };

  // ====================== INVENTORY ======================
  const fetchInventory = async (category = null) => {
    const config = {
      url: category ? `/minierp/inventory?category=${category}` : "/minierp/inventory",
      method: "get",
      headers: { Authorization: token }
    };
    return fetchData(config, { silent: true });
  };

  const createInventory = async (payload) => {
    const config = { url: "/minierp/inventory", method: "post", data: payload, headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };

  const updateInventory = async (id, payload) => {
    const config = { url: `/minierp/inventory/${id}`, method: "put", data: payload, headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };

  const toggleInventory = async (id) => {
    const config = { url: `/minierp/inventory/${id}/toggle`, method: "patch", headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };

  // ====================== VOUCHER ======================
  const fetchVouchers = async (type = null) => {
    const config = {
      url: type ? `/minierp/vouchers?type=${type}` : "/minierp/vouchers",
      method: "get",
      headers: { Authorization: token }
    };
    return fetchData(config, { silent: true });
  };

  const createVoucher = async (payload) => {
    const config = { url: "/minierp/vouchers", method: "post", data: payload, headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };

  const updateVoucher = async (id, payload) => {
    const config = { url: `/minierp/vouchers/${id}`, method: "put", data: payload, headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };

  const toggleVoucher = async (id) => {
    const config = { url: `/minierp/vouchers/${id}/toggle`, method: "patch", headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };

  // ====================== STOCK TRANSACTIONS ======================
  const fetchStockTransactions = async (type = null) => {
    const config = {
      url: type ? `/minierp/stock?type=${type}` : "/minierp/stock",
      method: "get",
      headers: { Authorization: token }
    };
    return fetchData(config, { silent: true });
  };

  const createStockTransaction = async (payload) => {
    const config = { url: "/minierp/stock", method: "post", data: payload, headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };

  const updateStockTransaction = async (id, payload) => {
    const config = { url: `/minierp/stock/${id}`, method: "put", data: payload, headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };

  // ====================== AUTHORISED USERS ======================
  const fetchAuthorisedUsers = async () => {
    const config = { url: "/minierp/users", method: "get", headers: { Authorization: token } };
    return fetchData(config, { silent: true });
  };

  const createAuthorisedUser = async (payload) => {
    const config = { url: "/minierp/users", method: "post", data: payload, headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };

  const updateAuthorisedUser = async (id, payload) => {
    const config = { url: `/minierp/users/${id}`, method: "put", data: payload, headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };

  const toggleAuthorisedUser = async (id) => {
    const config = { url: `/minierp/users/${id}/toggle`, method: "patch", headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };

  // ====================== PETTY TRANSACTIONS ======================
  const fetchPettyTransactions = async (type = null) => {
    const config = {
      url: type ? `/minierp/petty?type=${type}` : "/minierp/petty",
      method: "get",
      headers: { Authorization: token }
    };
    return fetchData(config, { silent: true });
  };

  const createPettyTransaction = async (payload) => {
    const config = { url: "/minierp/petty", method: "post", data: payload, headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };

  const updatePettyTransaction = async (id, payload) => {
    const config = { url: `/minierp/petty/${id}`, method: "put", data: payload, headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };
 
  const deletePettyTransaction = async (id) => {
    const config = { url: `/minierp/petty/${id}`, method: "delete", headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };
 

  // ====================== SEED DEFAULT LEDGERS ======================
  const seedDefaultLedgers = async () => {
    const config = { url: "/minierp/seed/default-ledgers", method: "post", headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };

  return {
    // Auth-derived identity
    userId,
    userName,
    userCode,

    // Accounting Period
    fetchAccountingPeriods,
    createAccountingPeriod,
    updateAccountingPeriod,
    toggleAccountingPeriod,

    // Ledger
    fetchLedgers,
    createLedger,
    updateLedger,
    toggleLedger,

    // Inventory
    fetchInventory,
    createInventory,
    updateInventory,
    toggleInventory,

    // Voucher
    fetchVouchers,
    createVoucher,
    updateVoucher,
    toggleVoucher,

    // Stock
    fetchStockTransactions,
    createStockTransaction,
    updateStockTransaction,

    // Authorised Users
    fetchAuthorisedUsers,
    createAuthorisedUser,
    updateAuthorisedUser,
    toggleAuthorisedUser,

    // Petty
    fetchPettyTransactions,
    createPettyTransaction,
    updatePettyTransaction,
    deletePettyTransaction,

    // Seeder
    seedDefaultLedgers,
  };
}
